import { useCallback, useMemo } from 'react'
import DataEditor, {
  GridCell,
  GridCellKind,
  GridColumn,
  Item,
  EditableGridCell,
} from '@glideapps/glide-data-grid'
import '@glideapps/glide-data-grid/dist/index.css'
import { MdAdd } from 'react-icons/md'
import {
  NetworkRateCardZone,
  ServiceIdentity,
  findService,
  getServiceKey,
  getServiceLabel,
  getUniqueServices,
} from './types'
import {
  isNeedsReviewPrefix,
  POSTCODE_STATUS_COLORS,
  POSTCODE_STATUS_GLYPHS,
} from './postcodeStatus'

interface Props {
  zones: NetworkRateCardZone[]
  onZoneNameChange: (zoneId: string, name: string) => void
  onDeleteZone: (zoneId: string) => void
  onAddZone: () => void
  onLocationCellClick: (zoneId: string) => void
  onPriceCellClick: (zoneId: string, service: ServiceIdentity) => void
  // True while the editor's auto-resolve batch is still in flight. When set, the location-cell
  // status check treats `!geoPoint` as "pending" rather than "failed" so the grid doesn't
  // flash every zone red during normal resolution.
  isAutoResolving?: boolean
}

export function NetworkZoneGrid({
  zones,
  onZoneNameChange,
  onDeleteZone,
  onAddZone,
  onLocationCellClick,
  onPriceCellClick,
  isAutoResolving = false,
}: Props) {
  const services = useMemo(() => getUniqueServices(zones), [zones])

  const columns: GridColumn[] = useMemo(() => {
    const cols: GridColumn[] = [
      { title: '', width: 48, id: 'actions' },
      { title: 'Zone', width: 160, id: 'zone' },
      { title: 'Location', width: 280, id: 'location' },
    ]
    for (const service of services) {
      cols.push({ title: getServiceLabel(service), width: 160, id: `service-${getServiceKey(service)}` })
    }
    return cols
  }, [services])

  const getLocationDisplay = useCallback(
    (zone: NetworkRateCardZone): string => {
      const prefixes = zone.locations.map((l) => l.prefix).slice(0, 8)
      let display = prefixes.join(', ')
      if (zone.locations.length > 8) {
        display += ` +${zone.locations.length - 8} more`
      }
      if (zone.locations.length === 0) {
        display = 'Click to add postcodes'
      }
      return display
    },
    []
  )

  const getPriceDisplay = useCallback(
    (zone: NetworkRateCardZone, service: ServiceIdentity): string => {
      const pricing = findService(zone, service)
      if (!pricing || pricing.breaks.length === 0) return '—'
      const firstBreak = pricing.breaks[0]
      const price = firstBreak.price
      if (price <= 0) return 'Set price'
      const display = `£${price.toFixed(2)}`
      return pricing.breaks.length > 1 ? `${display} •` : display
    },
    []
  )

  const getCellContent = useCallback(
    (cell: Item): GridCell => {
      const [col, row] = cell
      const zone = zones[row]
      if (!zone) {
        return { kind: GridCellKind.Text, data: '', displayData: '', allowOverlay: false }
      }

      // Actions column — always show ✕. Removing the last zone leaves an empty network
      // which save still rejects ("At least one zone is required"), but the visual was
      // misleading users into thinking the row was undeletable.
      if (col === 0) {
        return {
          kind: GridCellKind.Text,
          data: '✕',
          displayData: '✕',
          allowOverlay: false,
          themeOverride: { textDark: '#9CA3AF' },
        }
      }

      // Zone name column — click opens location panel (editable there)
      if (col === 1) {
        return {
          kind: GridCellKind.Text,
          data: zone.zoneName,
          displayData: zone.zoneName,
          allowOverlay: false,
        }
      }

      // Location column. Zone-level status tint + leading glyph so the user can spot trouble
      // across many zones without opening each LocationEditorPanel — and without relying on
      // colour alone (Glide cells are canvas, so we can't use aria; the glyph is the a11y signal).
      // While auto-resolve is in flight, treat !geoPoint as "pending" (keep blue) so the whole
      // grid doesn't flash red during normal resolution.
      if (col === 2) {
        const baseDisplay = getLocationDisplay(zone)
        if (zone.locations.length === 0) {
          return {
            kind: GridCellKind.Text,
            data: baseDisplay,
            displayData: baseDisplay,
            allowOverlay: false,
          }
        }
        const hasFailedLookups =
          !isAutoResolving && zone.locations.some((loc) => !loc.geoPoint)
        const hasNeedsReview =
          !hasFailedLookups && zone.locations.some((loc) => isNeedsReviewPrefix(loc.prefix))
        const status = hasFailedLookups
          ? 'failed'
          : hasNeedsReview
          ? 'needsReview'
          : 'resolved'
        const { bg: bgColor, text: textColor } = POSTCODE_STATUS_COLORS[status]
        const glyph =
          status === 'failed'
            ? POSTCODE_STATUS_GLYPHS.failed
            : status === 'needsReview'
            ? POSTCODE_STATUS_GLYPHS.needsReview
            : ''
        const display = glyph ? `${glyph} ${baseDisplay}` : baseDisplay
        return {
          kind: GridCellKind.Text,
          data: display,
          displayData: display,
          allowOverlay: false,
          themeOverride: { bgCell: bgColor, textDark: textColor },
        }
      }

      // Service price columns
      const serviceIndex = col - 3
      const service = services[serviceIndex]
      if (service) {
        const display = getPriceDisplay(zone, service)
        return {
          kind: GridCellKind.Text,
          data: display,
          displayData: display,
          allowOverlay: false,
          themeOverride: { bgCell: '#F0FDF4' },
        }
      }

      return { kind: GridCellKind.Text, data: '', displayData: '', allowOverlay: false }
    },
    [zones, services, getLocationDisplay, getPriceDisplay, isAutoResolving]
  )

  const onCellEdited = useCallback(
    (cell: Item, newValue: EditableGridCell) => {
      const [col, row] = cell
      const zone = zones[row]
      if (!zone) return

      // Only zone name is editable inline
      if (col === 1 && newValue.kind === GridCellKind.Text) {
        onZoneNameChange(zone.id, newValue.data)
      }
    },
    [zones, onZoneNameChange]
  )

  const onCellClicked = useCallback(
    (cell: Item) => {
      const [col, row] = cell
      const zone = zones[row]
      if (!zone) return

      // Actions column — delete
      if (col === 0) {
        onDeleteZone(zone.id)
        return
      }

      // Zone name column — open location editor
      if (col === 1) {
        onLocationCellClick(zone.id)
        return
      }

      // Location column — open location editor
      if (col === 2) {
        onLocationCellClick(zone.id)
        return
      }

      // Service columns — open price editor
      const serviceIndex = col - 3
      const service = services[serviceIndex]
      if (service) {
        onPriceCellClick(zone.id, service)
      }
    },
    [zones, services, onDeleteZone, onLocationCellClick, onPriceCellClick]
  )

  return (
    <div className='flex flex-col gap-2'>
      <div className='flex items-center justify-between'>
        <span className='text-sm text-gray-500'>
          {zones.length} zone{zones.length !== 1 ? 's' : ''} &middot;{' '}
          {services.length} service{services.length !== 1 ? 's' : ''}
        </span>
        <button
          type='button'
          className='flex items-center gap-1 px-3 py-1.5 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700 transition-colors'
          onClick={onAddZone}
        >
          <MdAdd className='w-4 h-4' />
          Add Zone
        </button>
      </div>
      <div className='border border-gray-200 rounded-lg overflow-hidden' style={{ height: Math.max(200, zones.length * 44 + 36) }}>
        <DataEditor
          getCellContent={getCellContent}
          columns={columns}
          rows={zones.length}
          onCellEdited={onCellEdited}
          onCellClicked={onCellClicked}
          rowMarkers='none'
          smoothScrollX
          smoothScrollY
          freezeColumns={3}
          headerHeight={36}
          rowHeight={44}
          getCellsForSelection={true}
        />
      </div>
    </div>
  )
}
